// Only the unpublished Atelier theme invokes these dedicated native Shopify codes.
// Shopify confirms eligibility, stock, discount allocations and checkout prices.
export const proposedPrice = cents => cents - Math.floor(cents * 10 / 100);
export function offerCodes(cart, code) {
  if (!/^VADROUK-SORTIE-10-\d{2}$/.test(code || '')) throw new Error('discount');
  const others = (cart.discount_codes || []).filter(d => d.applicable !== false || !/^VADROUK-SORTIE-10-\d{2}$/i.test(d.code)).map(d => d.code);
  const codes = [...new Map([...others, code].map(c => [c.toUpperCase(), c])).values()];
  if (codes.length > 5) throw new Error('offerLimit');
  return codes;
}
export function discountedExtras(cart, variants) {
  return variants.every(variant => {
    const lines = cart.items.filter(line => String(line.variant_id || line.id) === String(variant.id));
    return lines.length > 0 && lines.every(line => Number.isFinite(line.final_price) && line.final_price <= proposedPrice(variant.price));
  });
}
export const exactVariant = (variants, values) => variants.find(v => v.available && v.options.length === values.length && v.options.every((o, i) => o === values[i]));
export const matchingVariants = (variants, values) => variants.filter(v => v.available && v.options.every((o, i) => !values[i] || o === values[i]));
export function planItems(main, extras) {
  const items = [];
  if (main) {
    if (!main.variant?.available || !Number.isInteger(main.quantity) || main.quantity < 1) throw new Error('main');
    items.push({ id: main.variant.id, quantity: main.quantity });
  }
  if (!extras.length && !items.length) throw new Error('empty');
  for (const variant of extras) {
    if (!variant?.available) throw new Error('options');
    items.push({ id: variant.id, quantity: 1 });
  }
  return items;
}

const memories = new Map();
let eventsPromise;
let activeRequest = false;
let pendingChanges = 0;
let eventsReady = false;
function installEvents() {
  if (eventsPromise) return eventsPromise;
  eventsPromise = import('@shopify/events').then(({ StandardEvents }) => {
    for (const name of [StandardEvents.productSelect, StandardEvents.cartLinesUpdate]) {
      document.addEventListener(name, event => {
        pendingChanges++;
        refreshAll();
        Promise.resolve(event.promise).catch(() => {}).finally(() => {
          pendingChanges--;
          requestAnimationFrame(refreshAll);
        });
      }, true);
    }
    eventsReady = true;
    refreshAll();
  });
  return eventsPromise;
}
function refreshAll() { document.querySelectorAll('vadrouk-upsell-studio').forEach(el => el.refresh?.()); }
function updateImage(img, source, alt) {
  if (!img || !source) return;
  if (alt) img.alt = alt;
  const raw = typeof source === 'string' ? source : source.src;
  if (!raw) return;
  const url = new URL(raw, location.origin);
  url.searchParams.set('width', '240');
  if (img.getAttribute('src') !== url.href) {
    img.removeAttribute('srcset');
    img.src = url.href;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('vadrouk-upsell-studio')) {
  customElements.define('vadrouk-upsell-studio', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.texts = JSON.parse(this.querySelector('[data-vus-text]').textContent);
      this.cartContext = this.dataset.context === 'cart';
      this.source = this.querySelector('[data-vus-source]') ? JSON.parse(this.querySelector('[data-vus-source]').textContent) : null;
      this.memoryKey = `${this.dataset.context}:${this.dataset.source || 'cart'}`;
      this.format = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.cards = [...this.querySelectorAll('[data-vus-card]')].map(el => ({
        el, product: JSON.parse(el.querySelector('[data-vus-product]').textContent),
        checkbox: el.querySelector('[data-vus-select]'), selects: [...el.querySelectorAll('[data-vus-option]')]
      }));
      const memory = memories.get(this.memoryKey);
      for (const card of this.cards) {
        const saved = memory?.[card.product.id];
        card.checkbox.disabled = false;
        if (saved) {
          card.checkbox.checked = saved.selected;
          card.selects.forEach((s, i) => { if ([...s.options].some(o => o.value === saved.values[i])) s.value = saved.values[i]; });
        }
        card.checkbox.addEventListener('change', () => {
          this.setStatus(''); this.updateCard(card); this.remember(); this.refresh();
          if (card.checkbox.checked) card.selects.find(s => !s.value)?.focus({ preventScroll: true });
        });
        card.selects.forEach((select, index) => select.addEventListener('change', () => {
          this.setStatus('');
          this.updateCard(card, index);
          this.remember(); this.refresh();
        }));
        this.updateCard(card);
      }
      this.querySelector('[data-vus-edit-source]')?.addEventListener('click', () => {
        const form = this.mainForm();
        const section = form?.closest('.shopify-section');
        const picker = section?.querySelector('variant-picker');
        const target = picker?.querySelector('input:checked') || picker?.querySelector('select, button') || form?.querySelector('[type="submit"]');
        (picker || form)?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        target?.focus({ preventScroll: true });
      });
      this.querySelector('[data-vus-submit]').addEventListener('click', () => this.add());
      const rail = this.querySelector('.vus-cards');
      this.querySelector('[data-vus-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -292, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }));
      this.querySelector('[data-vus-next]')?.addEventListener('click', () => rail.scrollBy({ left: 292, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }));
      if (this.cartContext) {
        this.updateRail = () => {
          this.querySelector('[data-vus-scroll-controls]').hidden = rail.scrollWidth <= rail.clientWidth + 2;
          this.querySelector('[data-vus-prev]').disabled = rail.scrollLeft <= 2;
          this.querySelector('[data-vus-next]').disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
        };
        rail.addEventListener('scroll', this.updateRail, { passive: true });
        this.railObserver = new ResizeObserver(this.updateRail);
        this.railObserver.observe(rail);
        this.updateRail();
      }
      if (this.cartContext && this.cards.some(c => c.checkbox.checked)) this.closest('.vus-cart-panel')?.setAttribute('open', '');
      this.onChange = event => { if (!this.contains(event.target)) requestAnimationFrame(() => this.refresh()); };
      document.addEventListener('change', this.onChange);
      document.addEventListener('input', this.onChange);
      document.addEventListener('quantity-selector:update', this.onChange);
      this.onPageShow = event => { if (event.persisted) { this.completed = false; activeRequest = false; this.refresh(); } };
      window.addEventListener('pageshow', this.onPageShow);
      this.observer = new MutationObserver(() => this.refresh());
      const form = this.mainForm();
      if (form) this.observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      installEvents().catch(() => { this.setStatus(this.texts.reload); });
      this.refresh();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.railObserver?.disconnect();
      this.querySelector('.vus-cards')?.removeEventListener('scroll', this.updateRail);
      document.removeEventListener('change', this.onChange);
      document.removeEventListener('input', this.onChange);
      document.removeEventListener('quantity-selector:update', this.onChange);
      window.removeEventListener('pageshow', this.onPageShow);
      this.initialized = false;
    }
    money(cents) { return this.format.format(cents / 100); }
    write(selector, value) { const el = this.querySelector(selector); if (el && el.textContent !== value) el.textContent = value; }
    setStatus(message) { this.write('[data-vus-status]', message); }
    mainForm() {
      if (this.cartContext) return null;
      return this.closest('.buy-buttons-block')?.querySelector('product-form-component form') || document.querySelector(`product-form-component[data-product-id="${this.dataset.source}"] form`);
    }
    getMain() {
      if (this.cartContext) return null;
      const form = this.mainForm();
      const input = form?.querySelector('[name="id"]');
      const variant = this.source?.variants.find(v => String(v.id) === input?.value);
      const quantity = Number(form?.closest('product-form-component')?.querySelector('[name="quantity"]')?.value || 1);
      return { variant, quantity, valid: !!variant?.available && !!input && !input.disabled && !form.querySelector('[type="submit"]')?.disabled && Number.isInteger(quantity) && quantity > 0 };
    }
    values(card) { return card.selects.length ? card.selects.map(s => s.value) : card.product.variants[0].options; }
    remember() {
      memories.set(this.memoryKey, Object.fromEntries(this.cards.map(c => [c.product.id, { selected: c.checkbox.checked, values: c.selects.map(s => s.value) }])));
    }
    updateCard(card, changedIndex = -1) {
      const checked = card.checkbox.checked;
      card.el.classList.toggle('is-selected', checked);
      card.el.querySelector('[data-vus-configure]').hidden = !checked;
      card.el.querySelector('[data-vus-toggle-label]').textContent = checked ? this.texts.selected : this.texts.add;
      card.selects.forEach((select, index) => {
        select.disabled = !checked || activeRequest;
        const prefix = this.values(card).slice(0, index);
        for (const option of select.options) {
          option.disabled = !!option.value && !card.product.variants.some(v => v.available && v.options[index] === option.value && prefix.every((value, i) => !value || v.options[i] === value));
        }
        if (index > changedIndex && [...select.options].find(o => o.value === select.value)?.disabled) select.value = '';
      });
      const values = this.values(card);
      card.variant = exactVariant(card.product.variants, values);
      const candidates = matchingVariants(card.product.variants, values);
      const first = card.variant || [...candidates].sort((a, b) => a.price - b.price)[0];
      const preview = first || card.product.variants.find(v => v.available);
      const varies = !card.variant && new Set(candidates.map(v => v.price)).size > 1;
      const price = preview?.price;
      card.el.querySelector('[data-vus-price]').textContent = Number.isFinite(price) ? `${varies ? this.texts.from + ' ' : ''}${this.money(proposedPrice(price))}` : '—';
      card.el.querySelector('[data-vus-original]').textContent = Number.isFinite(price) ? this.money(price) : '';
      const imageVariant = card.variant || candidates.find(v => v.featured_image);
      updateImage(card.el.querySelector('.vus-product-image'), imageVariant?.featured_image || card.product.image, card.product.title + (imageVariant ? ' · ' + imageVariant.title : ''));
      const url = new URL(card.product.url, location.origin);
      if (card.variant) url.searchParams.set('variant', String(card.variant.id));
      card.el.querySelectorAll('a').forEach(a => { a.href = url.href; });
      const invalid = checked && !card.variant && values.every(Boolean);
      card.el.classList.toggle('is-invalid', invalid);
      card.el.querySelector('[data-vus-state]').textContent = card.variant ? this.texts.ready : invalid ? this.texts.unavailable : this.texts.optionsPending;
      card.selects.forEach(s => s.setAttribute('aria-invalid', String(invalid)));
    }
    refresh() {
      if (!this.isConnected || !this.cards) return;
      const main = this.getMain();
      if (main) {
        const variant = main.variant;
        this.write('[data-vus-source-price]', variant ? this.money(variant.price * main.quantity) : '—');
        this.write('[data-vus-source-options]', variant ? `${variant.title === 'Default Title' ? '' : variant.title}${main.quantity > 1 ? ' · ' + this.texts.quantity + ' : ' + main.quantity : ''}` : this.texts.sourceUnavailable);
        updateImage(this.querySelector('.vus-source-image'), variant?.featured_image || this.source.image, this.source.title + (variant ? ' · ' + variant.title : ''));
      }
      const chosen = this.cards.filter(c => c.checkbox.checked);
      this.dataset.selectedCount = String(chosen.length);
      const valid = chosen.every(c => c.variant?.available);
      const base = main?.valid ? main.variant.price * main.quantity : 0;
      const full = chosen.reduce((n, c) => n + (c.variant?.price || 0), base);
      const savings = chosen.reduce((n, c) => n + (c.variant ? c.variant.price - proposedPrice(c.variant.price) : 0), 0);
      const canEstimate = valid && (this.cartContext || main?.valid) && !pendingChanges;
      this.write('[data-vus-count]', `${chosen.length} ${chosen.length === 1 ? this.texts.counterOne : this.texts.counter}`);
      this.write('[data-vus-before]', canEstimate ? this.money(full) : '—');
      this.write('[data-vus-total]', canEstimate ? this.money(full - savings) : '—');
      this.querySelector('[data-vus-savings-row]').hidden = !canEstimate || !savings;
      this.write('[data-vus-savings]', this.money(savings));
      this.write('[data-vus-hint]', pendingChanges ? this.texts.sourcePending : !this.cartContext && !main?.valid ? this.texts.sourceUnavailable : !chosen.length ? this.texts.none : !valid ? this.texts.missing : this.texts.ready);
      const disabled = activeRequest || this.completed || !eventsReady || !!pendingChanges || !this.dataset.discountCode || (this.cartContext && !chosen.length) || !valid || (!this.cartContext && !main?.valid);
      const button = this.querySelector('[data-vus-submit]');
      button.disabled = disabled;
      button.textContent = this.completed ? this.texts.success : this.working ? this.texts.loading : this.cartContext ? this.texts.submitCart : chosen.length ? this.texts.submit : this.texts.submitMain;
      this.classList.toggle('is-busy', !!this.working);
      this.setAttribute('aria-busy', String(!!this.working));
      for (const card of this.cards) {
        card.checkbox.disabled = activeRequest || !!this.completed;
        card.selects.forEach(s => { s.disabled = !card.checkbox.checked || activeRequest || !!this.completed; });
      }
    }
    async add() {
      if (activeRequest || this.completed || !eventsReady || pendingChanges) return;
      const chosen = this.cards.filter(c => c.checkbox.checked);
      const main = this.getMain();
      if (!chosen.length) {
        if (!this.cartContext && main?.valid) {
          const form = this.mainForm();
          const submitter = form?.querySelector('[type="submit"]');
          if (submitter && !submitter.disabled) form.requestSubmit(submitter);
        } else this.setStatus(this.texts.pick);
        return;
      }
      if ((!this.cartContext && !main?.valid) || chosen.some(c => !c.variant?.available)) { this.setStatus(this.texts.missing); return; }
      activeRequest = true; this.working = true; this.setStatus(''); refreshAll();
      let posted = false;
      try {
        // Refresh server cart immediately before adding to avoid duplicate extras from another tab/drawer.
        const cartResponse = await fetch(`${this.dataset.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('cart');
        const cart = await cartResponse.json();
        const codes = offerCodes(cart, this.dataset.discountCode);
        if (pendingChanges || !this.isConnected) throw new Error('changed');
        const currentMain = this.getMain();
        if (!this.cartContext && !currentMain?.valid) throw new Error('main');
        const present = new Set(cart.items.map(i => String(i.product_id)));
        const extras = chosen.filter(c => !present.has(String(c.product.id)));
        // Complete the chosen set without adding the main product a second time if it is already present.
        const existingMainQuantity = currentMain ? cart.items.filter(i => String(i.variant_id) === String(currentMain.variant.id)).reduce((n, i) => n + i.quantity, 0) : 0;
        const missingMainQuantity = currentMain ? Math.max(0, currentMain.quantity - existingMainQuantity) : 0;
        const mainAddition = missingMainQuantity ? { variant: currentMain.variant, quantity: missingMainQuantity } : null;
        if (mainAddition || extras.length) {
          const items = planItems(mainAddition, extras.map(c => c.variant));
          const response = await fetch(`${this.dataset.root}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error('add');
        }
        posted = true;
        this.setStatus(this.texts.discountBusy);
        const discountResponse = await fetch(`${this.dataset.root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), attributes: { _vadrouk_upsell_source: this.dataset.sourceHandle } })
        });
        const discountedCart = await discountResponse.json();
        if (!discountResponse.ok || discountedCart.status || !discountedExtras(discountedCart, chosen.map(c => c.variant))) throw new Error('discount');
        memories.delete(this.memoryKey);
        this.completed = true;
        this.querySelector('[data-vus-success]').hidden = false;
        location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.setStatus(posted ? this.texts.discountError : error.message === 'offerLimit' ? this.texts.offerLimit : this.texts.error);
        // Always provide a cart recovery link; an interrupted response can have committed the add.
        const recovery = this.querySelector('[data-vus-success]');
        recovery.textContent = this.texts.openCart;
        // Keep other customer codes intact and do not add the same items twice.
        recovery.href = this.dataset.cartUrl;
        recovery.hidden = false;
      } finally {
        this.working = false;
        if (!posted) activeRequest = false;
        refreshAll();
      }
    }
  });
}
